import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. AuthCallback runs when the browser lands back at
 * `<origin>/auth/callback#session_id=...`. It exchanges that session_id for
 * our own JWT cookies via /api/auth/google/callback, then routes onward.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = /session_id=([^&]+)/.exec(hash);
    if (!match) {
      nav("/login", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(match[1]);
    // Wipe the fragment so a refresh doesn't re-run the exchange
    window.history.replaceState(null, "", window.location.pathname);

    (async () => {
      try {
        const { data } = await api.post("/auth/google/callback", { session_id });
        await refresh();
        if (data.needs_role_selection) {
          nav("/select-role", { replace: true, state: { user: data } });
          return;
        }
        const dest = data.role === "seller" ? "/seller" : data.role === "admin" ? "/admin" : "/discover";
        const stored = localStorage.getItem("vsk_next");
        if (data.role === "buyer" && stored && stored.startsWith("/")) {
          localStorage.removeItem("vsk_next");
          nav(stored, { replace: true });
          return;
        }
        nav(dest, { replace: true });
      } catch (e) {
        const msg = formatApiError(e.response?.data?.detail) || e.message;
        nav("/login", { replace: true, state: { authError: msg } });
      }
    })();
  }, [nav, refresh]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 text-[#007AFF] animate-spin mx-auto" />
        <p className="mt-4 text-neutral-400 text-sm">Signing you in with Google…</p>
      </div>
    </div>
  );
}
